(() => {
  'use strict';

  const ROOT = '[data-kefit-collection]';
  const FILTER_PREFIX = 'filter.';
  let requestSequence = 0;
  let activeController = null;

  function initAll(scope = document) {
    if (scope.matches?.(ROOT)) initCollection(scope);
    scope.querySelectorAll?.(ROOT).forEach(initCollection);
  }

  function initCollection(root) {
    if (!root || root.dataset.kefitReady === 'true') return;
    root.dataset.kefitReady = 'true';

    const sectionId = root.dataset.sectionId;
    const panel = root.querySelector('[data-kefit-filter-panel]');
    const form = root.querySelector('[data-kefit-filter-form]');
    const sort = root.querySelector('[data-kefit-sort-select]');

    root.querySelectorAll('[data-kefit-accordion-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const content = button.parentElement?.querySelector('.kefit-collection__filter-content');
        if (!content) return;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        content.classList.toggle('is-collapsed', expanded);
      });
    });

    root.querySelector('[data-kefit-filter-open]')?.addEventListener('click', () => {
      panel?.classList.add('is-open');
      document.documentElement.classList.add('kefit-collection-lock');
      document.body.style.overflow = 'hidden';
    });

    root.querySelectorAll('[data-kefit-filter-close]').forEach((button) => {
      button.addEventListener('click', () => closeMobileFilters(panel));
    });

    root.querySelectorAll('.kefit-collection__add-form').forEach((formElement) => {
      formElement.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = formElement.querySelector('.kefit-collection__add-button');
        if (!button || button.disabled) return;
        const original = button.innerHTML;
        button.disabled = true;
        button.innerHTML = '<span>' + translate('adding', 'Adding...') + '</span>';
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(formElement)
          });
          if (!response.ok) throw new Error('Unable to add product to cart.');
          await response.json();
          button.innerHTML = '<span>' + translate('added_to_cart', 'Added to cart') + '</span>';
          document.dispatchEvent(new CustomEvent('kefit:cart:added'));
        } catch (error) {
          console.error('[KEFIT Cart]', error);
          button.innerHTML = '<span>' + translate('try_again', 'Try again') + '</span>';
        } finally {
          window.setTimeout(() => {
            button.disabled = false;
            button.innerHTML = original;
          }, 1400);
        }
      });
    });

    root.querySelectorAll('[data-kefit-filter-input]').forEach((input) => {
      if (input.matches('[data-kefit-price-hidden-min], [data-kefit-price-hidden-max]')) return;
      input.addEventListener('change', () => applyFilters(root, form));
    });

    root.querySelectorAll('[data-kefit-price-slider]').forEach((slider) => {
      initPriceSlider(slider, () => {
        clearTimeout(slider._kefitTimer);
        slider._kefitTimer = window.setTimeout(() => applyFilters(root, form), 400);
      });
    });

    sort?.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      fetchAndRender(root, url, sectionId, true);
    });

    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-kefit-ajax-link]');
      if (!link || !root.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      fetchAndRender(root, new URL(link.href, window.location.origin), sectionId, true);
    });
  }

  function translate(key, fallback) {
    return typeof window.kefitT === 'function' ? window.kefitT(key, fallback) : fallback;
  }

  function closeMobileFilters(panel) {
    panel?.classList.remove('is-open');
    document.documentElement.classList.remove('kefit-collection-lock');
    document.body.style.overflow = '';
  }

  function initPriceSlider(slider, onChange) {
    const minInput = slider.querySelector('[data-kefit-price-range-min]');
    const maxInput = slider.querySelector('[data-kefit-price-range-max]');
    const fill = slider.querySelector('[data-kefit-price-fill]');
    const minLabel = slider.querySelector('[data-kefit-price-min-label]');
    const maxLabel = slider.querySelector('[data-kefit-price-max-label]');
    const selectedLabel = slider.querySelector('[data-kefit-price-selected]');
    const hiddenMin = slider.querySelector('[data-kefit-price-hidden-min]');
    const hiddenMax = slider.querySelector('[data-kefit-price-hidden-max]');
    if (!minInput || !maxInput || !hiddenMin || !hiddenMax) return;

    // Shopify's Liquid price-filter range values are already in cents.
    const min = Number(slider.dataset.rangeMin || 0);
    const max = Number(slider.dataset.rangeMax || 0);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return;

    const currentMin = clamp(Number(slider.dataset.currentMin || min), min, max);
    const currentMax = clamp(Number(slider.dataset.currentMax || max), min, max);
    const step = Math.max(1, Math.round((max - min) / 100));
    [minInput, maxInput].forEach((input) => {
      input.min = String(min);
      input.max = String(max);
      input.step = String(step);
    });
    minInput.value = String(Math.min(currentMin, currentMax));
    maxInput.value = String(Math.max(currentMin, currentMax));

    const money = (cents) => {
      const amount = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const whole = Math.round(cents / 100).toLocaleString();
      const format = slider.dataset.moneyFormat || '${{amount}}';
      return format
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole.replace(/,/g, 'TEMP').replace(/\./g, ',').replace(/TEMP/g, '.'))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(/,/g, 'TEMP').replace(/\./g, ',').replace(/TEMP/g, '.'))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    };

    const update = (notify = false, changedInput = null) => {
      let low = clamp(Number(minInput.value), min, max);
      let high = clamp(Number(maxInput.value), min, max);
      if (low > high) {
        if (changedInput === minInput) high = low;
        else low = high;
      }
      minInput.value = String(low);
      maxInput.value = String(high);
      const span = max - min;
      const lowPct = ((low - min) / span) * 100;
      const highPct = ((high - min) / span) * 100;
      if (fill) {
        fill.style.left = `${lowPct}%`;
        fill.style.right = `${100 - highPct}%`;
      }
      if (minLabel) minLabel.textContent = money(low);
      if (maxLabel) maxLabel.textContent = money(high);
      if (selectedLabel) selectedLabel.textContent = `${money(low)} – ${money(high)}`;
      hiddenMin.value = low > min ? (low / 100).toFixed(2) : '';
      hiddenMax.value = high < max ? (high / 100).toFixed(2) : '';
      if (notify) onChange();
    };

    minInput.addEventListener('input', () => update(true, minInput));
    maxInput.addEventListener('input', () => update(true, maxInput));
    update(false);
  }

  function clamp(value, min, max) {
    if (!Number.isFinite(value)) return min;
    return Math.min(max, Math.max(min, value));
  }

  function applyFilters(root, form) {
    if (!form) return;
    const url = new URL(window.location.href);
    // Remove only Shopify filter state and pagination; preserve unrelated query parameters.
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith(FILTER_PREFIX) || key === 'page' || key === 'section_id') url.searchParams.delete(key);
    });
    new URLSearchParams(new FormData(form)).forEach((value, key) => {
      if (value !== '') url.searchParams.append(key, value);
    });
    const sort = root.querySelector('[data-kefit-sort-select]');
    if (sort?.value) url.searchParams.set('sort_by', sort.value);
    fetchAndRender(root, url, root.dataset.sectionId, true);
  }

  async function fetchAndRender(root, pageUrl, sectionId, updateHistory) {
    if (!root || !sectionId) return;
    const requestId = ++requestSequence;
    if (activeController) activeController.abort();
    activeController = new AbortController();
    const controller = activeController;
    root.classList.add('is-loading');

    const requestedUrl = new URL(pageUrl, window.location.origin);
    const cleanUrl = new URL(requestedUrl);
    cleanUrl.searchParams.delete('section_id');
    const requestUrl = new URL(requestedUrl);
    requestUrl.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(requestUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const html = await response.text();
      if (requestId !== requestSequence) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = doc.querySelector(ROOT);
      if (!nextRoot) throw new Error('KEFIT collection section was not returned. Check the section_id and theme section markup.');

      nextRoot.dataset.kefitReady = 'false';
      root.replaceWith(nextRoot);
      initCollection(nextRoot);
      if (updateHistory) window.history.pushState({ kefitCollection: true }, '', cleanUrl.toString());
      closeMobileFilters(nextRoot.querySelector('[data-kefit-filter-panel]'));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[KEFIT Collection]', error);
        root.classList.remove('is-loading');
      }
    } finally {
      if (requestId === requestSequence) activeController = null;
    }
  }

  window.addEventListener('popstate', () => {
    const root = document.querySelector(ROOT);
    if (root) fetchAndRender(root, new URL(window.location.href), root.dataset.sectionId, false);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
